import React from 'react';

import { Picture } from '@shared/components/helpers/picture';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { IImage } from '@admin/types/image';

const IMAGE_SIZE = 480;

interface IItem {
  images: IImage[];
}

interface IHeroImagePickerProps {
  items: IItem[];
  selection?: IImage;
  onSelectionChange: (image: IImage) => void;
}

/**
 * Builds a flat list of images from all items, prepending the selection if not found.
 *
 * @param items - Array of items containing images
 * @param selection - Currently selected image (optional)
 * @returns Flattened array of images
 */
const buildImageList = (items: IItem[], selection?: IImage): IImage[] => {
  const allImages: IImage[] = [];
  items.forEach((item) => {
    item.images.forEach((image) => {
      allImages.push(image);
    });
  });
  if (selection) {
    const match = allImages.find((img) => img.id === selection.id);
    if (!match) {
      return [selection, ...allImages];
    }
  }
  return allImages;
};

/**
 * A component for selecting a hero image from a collection of items' images.
 * Displays a grid of images with radio buttons for selection.
 *
 * @param items - Array of items containing images
 * @param selection - Currently selected image
 * @param onSelectionChange - Callback when selection changes
 */
export const HeroImagePicker: React.FC<IHeroImagePickerProps> = ({ items, selection, onSelectionChange }) => {
  const images = buildImageList(items, selection);

  const handleChange = (image: IImage) => {
    onSelectionChange(image);
  };

  return (
    <div className="row" style={{ marginTop: '10px' }}>
      {images.map((image) => (
        <div key={image.id} className="col-lg-2 col-md-3 col-sm-4 col-xs-6" style={{ marginBottom: '15px' }}>
          <label
            className="brick panel"
            style={{
              cursor: 'pointer',
              margin: 0,
              border: selection?.id === image.id ? '2px solid #337ab7' : '1px solid #ddd',
              borderRadius: '4px',
              transition: 'border-color 0.2s ease',
            }}
          >
            <figure className="brick-square" style={{ margin: 0 }}>
              <Picture
                className="brick-image"
                source={image.state === 'uploaded' ? image.source : undefined}
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
                fit={ImgixFit.Fill}
                background={ImgixBG.White}
              />
            </figure>
            <div className="panel-footer text-center" style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
              <input
                id={`image-${image.id}`}
                type="radio"
                name="hero-image-selection"
                checked={selection?.id === image.id}
                onChange={() => handleChange(image)}
                aria-label={`Select hero image: ${image.name || 'image'}`}
                style={{ margin: 0 }}
              />
            </div>
          </label>
        </div>
      ))}
    </div>
  );
};
